import React, {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type ToastTone = "success" | "warning" | "error" | "info";

export interface ToastPayload {
  tone: ToastTone;
  title: string;
  body?: string | null;
  durationMs?: number;
}

interface ToastEntry extends ToastPayload {
  id: string;
  createdAt: number;
}

interface ToastContextValue {
  present(payload: ToastPayload): void;
  dismiss(id: string): void;
  toasts: ToastEntry[];
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const counterRef = useRef(0);
  const timeoutIdsRef = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    const timeoutId = timeoutIdsRef.current.get(id);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutIdsRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const present = useCallback(
    (payload: ToastPayload) => {
      counterRef.current += 1;
      const id = String(counterRef.current);
      const entry: ToastEntry = {
        body: payload.body ?? null,
        createdAt: Date.now(),
        durationMs: payload.durationMs ?? 6000,
        id,
        title: payload.title,
        tone: payload.tone,
      };
      setToasts((current) => [...current, entry]);
      const timeout = entry.durationMs;
      if (timeout && timeout > 0) {
        const timeoutId = window.setTimeout(() => dismiss(id), timeout);
        timeoutIdsRef.current.set(id, timeoutId);
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIds.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ dismiss, present, toasts }),
    [present, dismiss, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}

/**
 * Provider-optional variant for components that must also render in bare
 * test/harness contexts (e.g. renderToString without the app shell): returns
 * null instead of throwing, so toast reporting degrades to a no-op.
 */
export function useOptionalToast() {
  return useContext(ToastContext) ?? null;
}

const toneClassMap: Record<ToastTone, string> = {
  error:
    "border-rose-500/40 bg-rose-500/10 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/15 dark:text-rose-100",
  info: "border-slate-400/40 bg-slate-100 text-slate-900 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100",
  success:
    "border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100",
  warning:
    "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100",
};

const iconMap: Record<ToastTone, ReactNode> = {
  error: (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path
        d="M10 6v4m0 4h.01M18 10a8 8 0 11-16 0 8 8 0 0116 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
      />
    </svg>
  ),
  info: (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path
        d="M10 6h.01M9 9h1v5H9m9-4a8 8 0 11-16 0 8 8 0 0116 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
      />
    </svg>
  ),
  success: (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path
        d="M6 10.5l2.5 2.5L14 7.5m4 2.5a8 8 0 11-16 0 8 8 0 0116 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
      />
    </svg>
  ),
  warning: (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path
        d="M10 7v3m0 3h.01M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l5.58 9.92c.75 1.334-.214 2.98-1.742 2.98H4.42c-1.528 0-2.492-1.646-1.742-2.98l5.58-9.92z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
      />
    </svg>
  ),
};

const ToastViewport: FC<{
  toasts: ToastEntry[];
  onDismiss: (id: string) => void;
}> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) {
    return null;
  }
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-3 px-4 sm:items-end sm:px-6">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto w-full max-w-sm rounded-none border px-4 py-3 shadow-[8px_8px_0_rgba(15,23,42,0.12)] backdrop-blur dark:shadow-[10px_10px_0_rgba(2,6,23,0.35)] ${toneClassMap[toast.tone]}`}
        >
          <div className="flex items-start gap-3">
            <div className="border border-current/20 bg-white/40 p-1.5 dark:bg-black/10">
              {iconMap[toast.tone]}
            </div>
            <div className="flex-1">
              <p className="font-mono text-[11px] font-bold uppercase tracking-widest">
                {toast.title}
              </p>
              {toast.body && (
                <p className="mt-1 text-xs opacity-80">{toast.body}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] opacity-60 transition-opacity hover:opacity-100"
            >
              Close
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
